import { fmtDate, fmtMoney } from '../utils/invoiceUtils.js'
import { checkoutConfig as staticConfig } from '../config/checkout.js'
import { useProducts } from '../context/ProductsContext'
import './InvoiceDocument.css'

function DiscordCommunity({ discord }) {
  if (!discord?.inviteUrl) return null
  const serverName = discord.serverName || 'Discord'
  const ownerName = discord.ownerName?.trim() || 'the LooksLab team'

  return (
    <div className="invoice-no-print inv-discord">
      <div className="inv-discord__main">
        <p className="inv-discord__title">Join the {serverName} Community</p>
        <p className="inv-discord__body">
          {serverName} is where our community lives — free support, order updates, and direct access to the team.
        </p>
        {discord.inviteUrl !== '#' && (
          <a href={discord.inviteUrl} target="_blank" rel="noopener noreferrer" className="inv-discord__join">
            Join {serverName} &rarr;
          </a>
        )}
      </div>
      <div className="inv-discord__safety">
        <strong>Safety notice —</strong> {ownerName} will never personally DM you. All official communication happens
        inside the {serverName} server. If anyone contacts you outside of it claiming to represent LooksLab, it's a scam.
      </div>
    </div>
  )
}

/**
 * Shared printable invoice, used by both InvoicePage (/invoice) and
 * OrderConfirmPage (/order-confirm) — mirrors Prod's InvoiceDocument.jsx
 * contract (same prop names) so a real backend-fed order object renders
 * without changes.
 */
export default function InvoiceDocument({ order, activeConfig, isFresh = false, email = null, showDiscord = true, preorderMessage = null }) {
  const { branding } = useProducts()
  const usingFallbackLogo = !branding?.logoHoliday?.url && !branding?.logoWhite?.url
  const logoSrc = branding?.logoHoliday?.url ?? branding?.logoWhite?.url ?? '/logos/lookslab-logo.png'
  const config = activeConfig ?? staticConfig
  const company = config.company ?? { name: 'LooksLab', email: '', website: '' }
  const invCfg = config.invoice ?? {}
  const paymentMethods = config.paymentMethods ?? []
  const discord = config.discord ?? {}
  const billing = order.billingInfo ?? order.shipping ?? {}
  const hasPreorderFlag = order.items?.some((i) => i.preorder)

  return (
    <>
      <div className="invoice-no-print inv-toolbar">
        <button onClick={() => window.print()} className="inv-toolbar__btn">
          Save / Print PDF
        </button>
        <span className="inv-toolbar__hint">Use "Save as PDF" in the print dialog.</span>
      </div>

      <div className="inv-page">
        {isFresh && (
          <div className="invoice-no-print inv-success">
            <span className="inv-success__icon">&#10003;</span>
            <div>
              <p className="inv-success__title">Order placed successfully!</p>
              <p className="inv-success__body">
                {email && <>A copy has been sent to <strong>{email}</strong>. </>}
                Save or print your invoice using the button above.
              </p>
            </div>
          </div>
        )}

        <div className="invoice-root inv-root">
          <div className="inv-header">
            <img src={logoSrc} alt={company.name} className="inv-header__logo" style={usingFallbackLogo ? { filter: 'invert(1)' } : undefined} />
            <div className="inv-header__meta">
              <p className="inv-header__title">INVOICE</p>
              <p className="inv-header__number">{order.invoiceNumber}</p>
            </div>
          </div>

          <div className="inv-metarow">
            <div>
              <p className="inv-metarow__label">Invoice Date</p>
              <p className="inv-metarow__value">{fmtDate(order.createdAt ?? Date.now())}</p>
            </div>
            <div>
              <p className="inv-metarow__label">Due Date</p>
              <p className="inv-metarow__value">{order.dueAt ? fmtDate(order.dueAt) : '—'}</p>
            </div>
            <div>
              <p className="inv-metarow__label">Status</p>
              <p className="inv-metarow__value">{order.status === 'paid' ? 'Paid' : 'Awaiting Payment'}</p>
            </div>
          </div>

          <div className="inv-body">
            <div className="inv-bill-grid">
              <div>
                <p className="inv-section-label">Bill To</p>
                <p className="inv-bill-name">{billing.name ?? ([billing.firstName, billing.lastName].filter(Boolean).join(' ') || '—')}</p>
                <p className="inv-bill-lines">
                  {(email ?? billing.email) && <>{email ?? billing.email}<br /></>}
                  {billing.address}<br />
                  {billing.apt && <>{billing.apt}<br /></>}
                  {[billing.city, billing.state, billing.zip].filter(Boolean).join(', ')}<br />
                  {billing.country}
                </p>
              </div>
              <div>
                <p className="inv-section-label">From</p>
                <p className="inv-bill-name">{company.name}</p>
                <p className="inv-bill-lines">
                  {company.email}<br />
                  {company.website}
                </p>
              </div>
            </div>

            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr>
                    <th className="inv-table__th-left">Description</th>
                    <th>Conc.</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items ?? []).map((item, i) => {
                    const unitPrice = item.effectivePrice ?? item.price
                    return (
                      <tr key={item.lineId ?? i}>
                        <td className="inv-table__td-left">{item.name}</td>
                        <td className="inv-table__mono">{item.concentration ?? item.size ?? '—'}</td>
                        <td>{item.qty}</td>
                        <td>{fmtMoney(unitPrice)}</td>
                        <td className="inv-table__strong">{fmtMoney(unitPrice * item.qty)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="inv-totals-wrap">
              <div className="inv-totals">
                <div className="inv-totals__row">
                  <span>Subtotal</span>
                  <span>{fmtMoney(order.subtotal)}</span>
                </div>
                {(order.discountAmount ?? 0) > 0 && (
                  <div className="inv-totals__row inv-totals__row--credit">
                    <span>Discount {order.discountCode && `(${order.discountCode})`}</span>
                    <span>&minus;{fmtMoney(order.discountAmount)}</span>
                  </div>
                )}
                <div className="inv-totals__row">
                  <span>Shipping</span>
                  <span>{!order.shipping ? 'Free' : fmtMoney(order.shipping)}</span>
                </div>
                {(order.insuranceAmount ?? 0) > 0 && (
                  <div className="inv-totals__row">
                    <span>{order.insuranceLabel || 'Insurance'}</span>
                    <span>{fmtMoney(order.insuranceAmount)}</span>
                  </div>
                )}
                {/* Charged when the customer asks for in-stock items to ship
                    ahead of their pre-orders. It is part of Total Due, so it
                    has to be itemised here or the invoice does not add up. */}
                {(order.preorderShippingFee ?? 0) > 0 && (
                  <div className="inv-totals__row">
                    <span>{order.preorderShippingLabel || 'Pre-order second shipment'}</span>
                    <span>{fmtMoney(order.preorderShippingFee)}</span>
                  </div>
                )}
                {(order.redeemedAmount ?? 0) > 0 && (
                  <div className="inv-totals__row inv-totals__row--credit">
                    <span>Points redeemed{order.redeemedPoints ? ` (${order.redeemedPoints} pts)` : ''}</span>
                    <span>&minus;{fmtMoney(order.redeemedAmount)}</span>
                  </div>
                )}
                {(order.creditUsed ?? 0) > 0 && (
                  <div className="inv-totals__row inv-totals__row--credit">
                    <span>Store credit applied</span>
                    <span>&minus;{fmtMoney(order.creditUsed)}</span>
                  </div>
                )}
                <div className="inv-totals__final">
                  <span>Total Due</span>
                  <span>{fmtMoney(order.total)}</span>
                </div>
              </div>
            </div>

            {(hasPreorderFlag || preorderMessage) && (
              <div className="inv-notice">
                <p className="inv-notice__title">Pre-order Notice</p>
                <p className="inv-notice__body">
                  {preorderMessage ??
                    'This order includes one or more pre-order items. In-stock items ship first — pre-order items ship separately once available.'}
                </p>
              </div>
            )}

            {paymentMethods.length > 0 && (
              <div className="inv-payment">
                <p className="inv-section-label">Payment Instructions</p>
                <p className="inv-payment__intro">
                  Include invoice number <strong>{order.invoiceNumber}</strong> in the memo/reference when sending payment.
                </p>
                <div className="inv-payment__list">
                  {paymentMethods.map((pm) => (
                    <div key={pm.id} className="inv-payment__method">
                      <p className="inv-payment__label">{pm.label}</p>
                      <p className="inv-payment__instructions">{pm.instructions}</p>
                      <p className="inv-payment__details">{pm.details}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="inv-footer">
              <p><strong>Research Use Only:</strong> {invCfg.notes}</p>
              <p className="inv-footer__thanks">{invCfg.thankYouMessage}</p>
            </div>
          </div>
        </div>

        {showDiscord && <DiscordCommunity discord={discord} />}
      </div>
    </>
  )
}
